import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import AvatarCropper from '@/components/AvatarCropper';
import RelationshipModal from '@/components/RelationshipModal';
import AdultGate from '@/components/AdultGate';
import FounderBadge from '@/components/FounderBadge';
import CalendarConnectCard from '@/components/CalendarConnectCard';
import AuroraOptOutToggle from '@/components/AuroraOptOutToggle';
import DangerZone from '@/components/DangerZone';
import { Crown, Lightning, FireSimple, Trophy, Copy, Plus, Diamond, Camera, Heart, Shield, Star } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, color }) => (
  <div className="glass rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
    <div className={`font-display text-2xl font-black mt-1 ${color}`}>{value}</div>
  </div>
);

const Profile = () => {
  const { user, refresh } = useAuth();
  const [invites, setInvites] = useState({ invites: [], remaining: 0 });
  const [trinkets, setTrinkets] = useState([]);
  const [bio, setBio] = useState(user?.bio || '');
  const [city, setCity] = useState(user?.city || '');
  const [cropperOpen, setCropperOpen] = useState(false);
  const [relOpen, setRelOpen] = useState(false);
  const [adultOpen, setAdultOpen] = useState(false);
  const [tourBusy, setTourBusy] = useState(false);
  const [relInfo, setRelInfo] = useState(null);

  const loadRel = async () => {
    try {
      const { data } = await api.get('/auth/profile/relationship');
      setRelInfo(data);
    } catch { /* silent */ }
  };
  useEffect(() => { loadRel(); }, []);

  const createTour = async () => {
    const name = prompt('Tour Bubble name (e.g. AURORA festival 2026):');
    if (!name) return;
    setTourBusy(true);
    try {
      const { data } = await api.post('/admin/tour-bubbles',
        { name, tier: 'festival', prize_kind: 'ticket', duration_days: 21 });
      toast.success(`Tour "${data.tour.brand_name}" launched.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not');
    } finally { setTourBusy(false); }
  };

  const load = async () => {
    const { data } = await api.get('/invites/mine');
    setInvites(data);
    const t = await api.get('/trinkets/mine');
    setTrinkets(t.data.trinkets);
  };
  useEffect(() => { load(); }, []);

  const gen = async () => {
    try {
      const { data } = await api.post('/invites/generate');
      toast.success(`Invite ${data.code} ready`);
      load(); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'cannot generate'); }
  };

  const copy = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success('Code copied');
  };

  const save = async () => {
    try {
      await api.put('/auth/profile', { bio, city });
      toast.success('Profile saved');
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'failed'); }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="glass rounded-2xl p-8">
        <div className="flex items-start gap-6">
          <button
            onClick={() => setCropperOpen(true)}
            data-testid="profile-avatar-btn"
            className="relative h-24 w-24 rounded-full overflow-hidden group ring-2 ring-white/10 hover:ring-bx-cyan transition"
            title="Change avatar"
          >
            {user.avatar_url ? (
              <img src={`${BACKEND_URL}${user.avatar_url}`} alt={user.username}
                   className="h-full w-full object-cover"
                   data-testid="profile-avatar-img" />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe grid place-items-center font-display font-black text-3xl">
                {user.username?.[0]?.toUpperCase()}
              </div>
            )}
            <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition grid place-items-center">
              <Camera size={20} weight="bold" className="text-white" />
            </span>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">@{user.username} <FounderBadge user={user} size={16} className="ml-1" /></h1>
              {user.founder_number <= 5000 && (
                <span className="chip text-bx-gold border-bx-gold/40">
                  <Crown size={12} weight="fill" /> Founder #{user.founder_number}
                </span>
              )}
            </div>
            <div className="mt-2 text-white/60 text-sm">{user.email}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(user.badges || []).map((b) => (
                <span key={b} className="chip text-bx-cyan border-bx-cyan/30">{b}</span>
              ))}
              {(user.gems || []).map((g) => (
                <span key={g.id} className={`chip rar-${g.rarity}`}>
                  {g.emoji} {g.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-8">
          <Stat label="Level" value={user.level} color="text-bx-cyan" />
          <Stat label="XP" value={user.xp.toLocaleString()} color="text-bx-volt" />
          <Stat label="Energy" value={user.energy} color="text-bx-magenta" />
          <Stat label="Streak" value={`${user.streak}d`} color="text-bx-gold" />
          <Stat label="Raised" value={`$${(user.total_raised_usd || 0).toFixed(2)}`} color="text-bx-magenta" />
        </div>
      </div>

      <CalendarConnectCard user={user} />
      <AuroraOptOutToggle />

      {/* Relationship card */}
      <div className="glass rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Heart size={20} weight="duotone" className="text-bx-magenta" /> Relationship
          </h2>
          <button data-testid="open-relationship"
            onClick={() => setRelOpen(true)}
            className="text-bx-cyan text-xs font-bold uppercase tracking-widest hover:underline">
            edit →
          </button>
        </div>
        <div className="text-sm text-white/70">
          {relInfo?.me?.relationship_status ? (
            <>
              <span className="text-bx-magenta font-bold capitalize">{relInfo.me.relationship_status.replace('_',' ')}</span>
              {relInfo.partner?.username && (
                <> · with <Link to={`/creator/${relInfo.partner.username}`} className="text-bx-cyan font-bold">@{relInfo.partner.username}</Link></>
              )}
              {relInfo.me?.partner_pending_from && !relInfo.partner && (
                <span className="ml-2 text-amber-300 text-xs">waiting for them to confirm</span>
              )}
            </>
          ) : (
            <span className="text-white/65">
              Tell us your status — it unlocks lover bubbles, date nights, and chance-crush drama.
            </span>
          )}
        </div>
      </div>

      {/* Adult corner / Tour admin actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <button
          data-testid="open-adult"
          onClick={() => setAdultOpen(true)}
          className="glass rounded-2xl p-4 text-left hover:bg-white/[0.04] transition flex items-center gap-3">
          <Shield size={20} weight="duotone" className={user?.is_age_verified ? 'text-bx-volt' : 'text-bx-magenta'} />
          <div className="flex-1">
            <div className="font-bold text-sm">
              {user?.is_age_verified ? 'Verified 21+' : 'Enter the after-dark world'}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">
              {user?.is_age_verified ? 'verified' : 'tap to verify your age'}
            </div>
          </div>
        </button>
        {user?.is_admin && (
          <button
            data-testid="admin-create-tour"
            onClick={createTour} disabled={tourBusy}
            className="glass rounded-2xl p-4 text-left hover:bg-white/[0.04] transition flex items-center gap-3">
            <Star size={20} weight="duotone" className="text-bx-gold" />
            <div className="flex-1">
              <div className="font-bold text-sm">{tourBusy ? 'launching…' : 'Launch a Tour Bubble'}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">admin · drift through network</div>
            </div>
          </button>
        )}
      </div>

      {/* Trinkets & Gems */}
      <div className="glass rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Diamond size={20} weight="duotone" className="text-bx-magenta" /> Trinkets & Gems
          </h2>
          <Link to="/trinkets" data-testid="profile-shop-trinkets" className="text-bx-cyan text-xs font-bold uppercase tracking-widest">Shop →</Link>
        </div>
        {trinkets.length === 0 ? (
          <div className="text-white/60 text-sm">No trinkets yet. Mint your first one to add status.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {trinkets.map((t) => (
              <div key={t.id} data-testid={`profile-trinket-${t.id}`} className="glass-hi rounded-xl p-3 text-center">
                <div className="text-3xl">{t.emoji}</div>
                <div className="text-[10px] uppercase tracking-widest mt-1 truncate">{t.name}</div>
                <div className={`text-[10px] rar-${t.rarity}`}>{t.rarity}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile edit */}
      <div className="glass rounded-2xl p-6 mt-6">
        <h2 className="font-display text-2xl font-bold mb-4">Edit Profile</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Bio</span>
            <textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={2} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm resize-none" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">City</span>
            <input data-testid="profile-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>
          <button data-testid="profile-save" onClick={save} className="btn-primary px-5 py-2.5 rounded-full text-sm">Save changes</button>
        </div>
      </div>

      {/* Invites */}
      <div className="glass rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold">Your Invites</h2>
          <button data-testid="invite-generate" onClick={gen} disabled={invites.remaining <= 0} className="btn-volt px-4 py-2 rounded-full text-sm flex items-center gap-1.5 disabled:opacity-40">
            <Plus size={14} weight="bold" /> Generate ({invites.remaining} left)
          </button>
        </div>
        <div className="space-y-2">
          {invites.invites.length === 0 && <div className="text-white/60 text-sm">No invites generated yet.</div>}
          {invites.invites.map((i) => (
            <div key={i.code} data-testid={`invite-${i.code}`} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-3">
              <code className="font-mono text-sm text-bx-cyan">{i.code}</code>
              <div className="flex items-center gap-3">
                <span className="text-xs text-white/60">{i.uses_remaining > 0 ? 'available' : 'used'}</span>
                <button onClick={() => copy(i.code)} className="text-white/60 hover:text-white"><Copy size={14} weight="bold" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AvatarCropper open={cropperOpen} onClose={() => setCropperOpen(false)} />
      <RelationshipModal open={relOpen} onClose={() => setRelOpen(false)} onSaved={loadRel} />
      <AdultGate open={adultOpen} onClose={() => setAdultOpen(false)} onVerified={() => { refresh?.(); }} />

      {/* GDPR / CCPA — your data, your call */}
      <DangerZone />
    </div>
  );
};

export default Profile;
